import { supabase } from "@/lib/supabase";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import type { DateRangeOption } from "@/components/clicks/DateRangeFilter";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import {
  type CampaignStat,
  type PipelineFreshnessRow,
} from "@/types/clicks";

export type DomainPagesParams = {
  domain: string;
  /** The dimension the table is grouped by, and the value of the row itself. */
  dim: string;
  value: string;
  dateRange: DateRangeOption;
  customFrom?: string;
  customTo?: string;
  activeFilters: UrlActiveFilters;
};

/** A filter field's value with its clicks over the last 30 days (search_filter_values); no clicks: not known. */
export type FilterValue = { value: string; clicks?: number; lastSeen: string | null };

/** A row mark (20261030183128): shared, the last one who set it and when. */
export type ClicksMark = { value: string; mark: string; by: string | null; at: string };

/** An RPC error with PostgREST's code kept - PGRST202 is "not deployed yet". */
export class RpcError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

export type ClicksStatsResult = {
  data: CampaignStat[];
  previousData: CampaignStat[] | null;
  totalCount: number;
};

/** One point of the daily/weekly/monthly Keitaro trend (get_clicks_trend). */
export type ClicksTrendPoint = {
  bucket_date: string;
  total_clicks: number;
  unique_global: number;
  total_conversions: number;
  total_leads: number;
  total_sales: number;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
  total_bots: number;
};

export type ClicksTrendGranularity = "day" | "week" | "month";

/**
 * Upper bound on rows fetched for the client-side grid. Covers the real
 * groupings (domains ~9.7k, city×brand×country ~9k) with headroom; past this the
 * table would truncate, but no grouping reaches it today.
 */
export const ALL_STATS_MAX_ROWS = 20000;

/** One point of the GSC trend, aggregated across domains for the chart. */
export type GscChartPoint = {
  bucket_date: string;
  gsc_clicks: number;
  gsc_impressions: number;
  gsc_ctr: number;
  gsc_position: number;
};

type LooseResult = {
  data: unknown;
  error: { message: string; code?: string } | null;
  count: number | null;
};
interface LooseBuilder extends PromiseLike<LooseResult> {
  range: (from: number, to: number) => PromiseLike<LooseResult>;
}

/**
 * RPC escape hatch for calls the generated types can't express yet: p_dims isn't
 * in the generated get_dynamic_stats args (types lag the migration), and date
 * args are typed non-null though the functions accept null for "no lower bound".
 * Structural cast — no @ts-expect-error to rot — response is cast at each site.
 */
const looseRpc = (
  fn: string,
  args: Record<string, unknown>,
  opts?: { count?: "exact" | "planned" | "estimated" },
): LooseBuilder =>
  (
    supabase.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
      opts?: unknown,
    ) => LooseBuilder
  )(fn, args, opts);

/**
 * ponytail: stopgap until 20261027200544 is on prod - without it PostgREST
 * answers PGRST202 and the filter editor showed only an error. The old
 * get_filter_options list (every value of nine fields, ~1 MB, no clicks) is
 * searched here instead, loaded once; url, region, city and language have no
 * list there and come back empty. Delete this and get_filter_options once
 * search_filter_values is deployed.
 */
const LEGACY_OPTION_KEYS: Record<string, string> = {
  campaign_name: "campaigns",
  brand: "brands",
  domain: "domains",
  source: "sources",
  campaign_group: "groups",
  stream: "streams",
  offer: "offers",
  affiliate_network: "networks",
  country: "geos",
};
let legacyOptions: Promise<Record<string, string[] | undefined>> | undefined;
const loadLegacyOptions = async () => {
  const { data, error } = await supabase.rpc("get_filter_options");
  if (error) throw new Error(error.message);
  return (data ?? {}) as Record<string, string[] | undefined>;
};
const searchLegacyOptions = async (field: string, query: string, limit: number): Promise<FilterValue[]> => {
  const key = LEGACY_OPTION_KEYS[field];
  if (!key) return [];
  legacyOptions ??= loadLegacyOptions().catch((e) => {
    legacyOptions = undefined; // a failed load is retried, not cached
    throw e;
  });
  const q = query.trim().toLowerCase();
  const rank = (v: string) => (v.toLowerCase() === q ? 0 : v.toLowerCase().startsWith(q) ? 1 : 2);
  return ((await legacyOptions)[key] ?? [])
    .filter((v) => v.toLowerCase().includes(q))
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, limit)
    .map((value) => ({ value, lastSeen: null }));
};

class ClicksService {
  private static instance: ClicksService;

  private constructor() {}

  public static getInstance(): ClicksService {
    if (!ClicksService.instance) {
      ClicksService.instance = new ClicksService();
    }
    return ClicksService.instance;
  }

  /**
   * Every row for the current grouping/dates/filters, unpaginated — the grid
   * sorts, filters and pages these in the browser so those act on the whole
   * result, not just one server page. Bounded: a grouping yields thousands of
   * rows at most (campaigns ~2k, domains ~9k), not the raw click log.
   */
  public async getAllStats(params: {
    groupBy: string;
    /** All grouping dimensions; 2+ triggers multi-group via p_dims. */
    groupDims?: string[];
    dateRange: DateRangeOption;
    compareEnabled: boolean;
    customFrom?: string;
    customTo?: string;
    activeFilters: UrlActiveFilters;
  }): Promise<ClicksStatsResult> {
    const { current, previous } = getDateRangeDates(
      params.dateRange,
      new Date(),
      params.customFrom,
      params.customTo,
    );

    // p_dims only when grouping by 2+ dimensions — a single dim stays the plain
    // 4-arg call, which works whether or not the multi-dim migration is deployed.
    const dims = params.groupDims ?? [params.groupBy];
    const pDims = dims.length >= 2 ? dims : undefined;

    const argsFor = (start?: string, end?: string) => ({
      dim: params.groupBy,
      p_start_date: start ?? undefined,
      p_end_date: end,
      p_filters: params.activeFilters,
      ...(pDims ? { p_dims: pDims } : {}),
    });

    const [data, previousData] = await Promise.all([
      this.fetchAllStatRows(argsFor(current.start ?? undefined, current.end)),
      params.compareEnabled
        ? this.fetchAllStatRows(argsFor(previous.start ?? undefined, previous.end))
        : Promise.resolve(null),
    ]);

    return { data, previousData, totalCount: data.length };
  }

  /**
   * Fetch every row of a get_dynamic_stats call, in one request.
   *
   * get_dynamic_stats_all returns the whole result as one jsonb array, so the
   * 1000-row cap (PostgREST max_rows) does not apply and the aggregation runs
   * once. Paging the plain function instead re-ran the whole aggregation for
   * every 1000 rows, in parallel: domains over All time (10k rows) meant 11
   * identical heavy queries that starved each other past the 8 s timeout and
   * the table came back empty (08.10.2026, migration 20261008140000).
   */
  private async fetchAllStatRows(
    args: Record<string, unknown>,
  ): Promise<CampaignStat[]> {
    const all = await looseRpc("get_dynamic_stats_all", {
      ...args,
      p_max_rows: ALL_STATS_MAX_ROWS,
    });
    if (!all.error) return (all.data ?? []) as CampaignStat[];
    // A real failure (timeout) must surface; only a missing function falls back.
    if (all.error.code !== "PGRST202") {
      console.error("Error loading clicks stats:", all.error);
      throw new Error(all.error.message);
    }
    return this.fetchStatRowsPaged(args);
  }

  /**
   * ponytail: fallback until migration 20261008140000 is deployed - pages the
   * plain function and has the parallel re-aggregation problem above. Drop
   * after the push.
   */
  private async fetchStatRowsPaged(
    args: Record<string, unknown>,
  ): Promise<CampaignStat[]> {
    const PAGE = 1000;
    const first = await looseRpc("get_dynamic_stats", args, {
      count: "exact",
    }).range(0, PAGE - 1);

    if (first.error) {
      console.error("Error loading clicks stats:", first.error);
      throw new Error(first.error.message);
    }

    let rows = (first.data ?? []) as CampaignStat[];
    const total = Math.min(first.count ?? rows.length, ALL_STATS_MAX_ROWS);
    if (total <= PAGE) return rows;

    // One page at a time: every page re-runs the whole aggregation, and in
    // parallel they starved each other past the timeout. Slow, but it loads.
    for (let start = PAGE; start < total; start += PAGE) {
      const r = await looseRpc("get_dynamic_stats", args).range(
        start,
        Math.min(start + PAGE, total) - 1,
      );
      if (r.error) throw new Error(r.error.message);
      rows = rows.concat((r.data ?? []) as CampaignStat[]);
    }
    return rows;
  }

  /**
   * Daily/weekly/monthly Keitaro trend for the chart, from get_clicks_trend
   * (which reuses the table's filter logic, so the two agree). Never throws:
   * the RPC may not be deployed yet (draft in supabase/pending/) — then it
   * returns [] and the chart shows an empty state instead of breaking.
   */
  public async getClicksTrend(params: {
    activeFilters: UrlActiveFilters;
    granularity: ClicksTrendGranularity;
    /** The range to plot — the chart's own, or the table's when linked. */
    dateRange: DateRangeOption;
    customFrom?: string;
    customTo?: string;
  }): Promise<ClicksTrendPoint[]> {
    // One explicit window, resolved exactly like the table resolves its own —
    // so "linked to table" lands on the same days the table shows. all_time has
    // no lower bound (start = null): the RPC then plots from the first click on.
    const { current } = getDateRangeDates(
      params.dateRange,
      new Date(),
      params.customFrom,
      params.customTo,
    );
    // null, not undefined: all_time has no lower bound (start = null), and the
    // RPC's p_date_from has no DB default — omitting it makes PostgREST fail to
    // resolve the function (404). Sending null matches, and the function treats
    // null as "from the first click on".
    const from = current.start;
    const to = current.end;

    // Cast: get_clicks_trend is not in the generated DB types until it is
    // deployed and types are regenerated. Structural cast keeps this compiling
    // now and after — no @ts-expect-error to rot.
    const client = supabase as unknown as {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };

    const { data, error } = await client.rpc("get_clicks_trend", {
      p_date_from: from,
      p_date_to: to,
      p_granularity: params.granularity,
      p_filters: params.activeFilters,
    });

    if (error) {
      console.warn(
        "Clicks trend unavailable (get_clicks_trend not deployed yet?):",
        error.message,
      );
      return [];
    }

    return (data ?? []) as ClicksTrendPoint[];
  }

  /**
   * GSC trend for the chart, aggregated across domains per bucket. get_gsc_trend
   * returns one row per (bucket, domain); we sum clicks/impressions, and rebuild
   * CTR and average position from the totals (you can't average CTRs or average
   * positions — position is weighted by impressions). Fail-soft: [] on error.
   *
   * Caveat kept in mind: GSC counts in Pacific time and lags 2-3 days, and a GSC
   * "click" is a search click, not a Keitaro tracker hit — the chart labels the
   * two separately for that reason.
   */
  public async getGscTrend(params: {
    granularity: ClicksTrendGranularity;
    dateRange: DateRangeOption;
    customFrom?: string;
    customTo?: string;
    /** Scope to one domain (drill-down); omitted = all covered domains. */
    domain?: string;
  }): Promise<GscChartPoint[]> {
    const { current } = getDateRangeDates(
      params.dateRange,
      new Date(),
      params.customFrom,
      params.customTo,
    );

    // looseRpc: p_date_from is null for all_time (no lower bound), but the
    // generated type insists on string.
    const { data, error } = await looseRpc("get_gsc_trend", {
      p_date_from: current.start,
      p_date_to: current.end,
      p_granularity: params.granularity,
      p_domains: params.domain ? [params.domain] : undefined,
      p_filters: {},
    });

    if (error) {
      console.warn("GSC trend unavailable:", error.message);
      return [];
    }

    type Row = {
      bucket_date: string;
      clicks: number | string | null;
      impressions: number | string | null;
      avg_position: number | string | null;
    };
    const agg = new Map<
      string,
      { clicks: number; impressions: number; posWeighted: number }
    >();
    for (const r of (data ?? []) as Row[]) {
      const clicks = Number(r.clicks) || 0;
      const impressions = Number(r.impressions) || 0;
      const position = Number(r.avg_position) || 0;
      const cur = agg.get(r.bucket_date) ?? {
        clicks: 0,
        impressions: 0,
        posWeighted: 0,
      };
      cur.clicks += clicks;
      cur.impressions += impressions;
      cur.posWeighted += position * impressions;
      agg.set(r.bucket_date, cur);
    }

    return [...agg.entries()]
      .map(([bucket_date, a]) => ({
        bucket_date,
        gsc_clicks: a.clicks,
        gsc_impressions: a.impressions,
        gsc_ctr: a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0,
        gsc_position: a.impressions > 0 ? a.posWeighted / a.impressions : 0,
      }))
      .sort((x, y) => x.bucket_date.localeCompare(y.bucket_date));
  }

  /**
   * The pages a domain received traffic on, as full stat rows.
   *
   * This is get_dynamic_stats grouped by url with a domain filter added: the
   * page breakdown is just "the URL rows of this one domain". Returning the same
   * CampaignStat shape as the table lets the expanded rows render under the very
   * same columns as their parent (and honour the Metrics picker) instead of a
   * separate mini-table with a fixed handful of columns.
   *
   * Dates and filters are resolved exactly as getStats resolves them, from the
   * same params — this list hangs under a row and has to add up to it, so it
   * must not pick its own window or quietly ignore the sidebar.
   */
  public async getDomainPages(
    params: DomainPagesParams,
  ): Promise<CampaignStat[]> {
    const { current } = getDateRangeDates(
      params.dateRange,
      new Date(),
      params.customFrom,
      params.customTo,
    );

    // The parent's own domain, added on top of the sidebar filters. include, so
    // it narrows to exactly this domain's URLs even if a broader domain filter
    // is already set.
    const filters: UrlActiveFilters = {
      ...params.activeFilters,
      domain: { mode: "include", values: [params.domain] },
    };

    const { data, error } = await supabase.rpc("get_dynamic_stats", {
      dim: "url",
      p_start_date: current.start ?? undefined,
      p_end_date: current.end,
      p_filters: filters,
    });

    if (error) {
      console.error("Error loading domain pages:", error);
      throw new Error(error.message);
    }

    return (data ?? []) as CampaignStat[];
  }

  public async getPipelineFreshness(): Promise<PipelineFreshnessRow[]> {
    const { data, error } = await supabase.rpc("get_pipeline_freshness");

    if (error) {
      console.error("Error loading pipeline freshness:", error);
      throw new Error(error.message);
    }

    return (data ?? []) as PipelineFreshnessRow[];
  }

  /**
   * One filter field's values matching `query`, best first (exact, prefix,
   * then clicks over 30 days) - search_filter_values (20261027200544). Empty
   * query: the field's top values.
   */
  public async searchFilterValues(field: string, query: string, limit = 50): Promise<FilterValue[]> {
    const { data, error } = await looseRpc("search_filter_values", { p_dim: field, p_q: query, p_limit: limit });
    if (error?.code === "PGRST202") return searchLegacyOptions(field, query, limit);
    if (error) throw new Error(error.message);
    return ((data ?? []) as { v: string; n: number | string | null; last_seen: string | null }[]).map((r) => ({
      value: r.v,
      clicks: Number(r.n ?? 0),
      lastSeen: r.last_seen,
    }));
  }

  /** Every mark on one field's values (get_clicks_marks). */
  public async getMarks(field: string): Promise<ClicksMark[]> {
    const { data, error } = await looseRpc("get_clicks_marks", { p_dim: field });
    if (error) throw new RpcError(error.message, error.code);
    return ((data ?? []) as { v: string; mark: string; by: string | null; at: string }[]).map((r) => ({
      value: r.v,
      mark: r.mark,
      by: r.by,
      at: r.at,
    }));
  }

  /**
   * One attribute source (ATTRIBUTE_SOURCES): normalized domain -> the values
   * in its columns' order, for every domain the user may see - e.g.
   * get_domain_attributes (20261103123525), one ~1.6 MB answer, read while one
   * of its columns is shown.
   */
  public async getAttributes(rpc: string): Promise<Map<string, unknown[]>> {
    const { data, error } = await looseRpc(rpc, {});
    if (error) throw new RpcError(error.message, error.code);
    return new Map(Object.entries((data ?? {}) as Record<string, unknown[]>));
  }

  /**
   * The filter modes the server knows (clicks_filter_modes). An older server
   * would read "contains" as "is not" (clicks_filter_sql's else branch), so
   * the bar offers the text modes only once this says so.
   */
  public async getFilterModes(): Promise<string[]> {
    const { data, error } = await looseRpc("clicks_filter_modes", {});
    if (error) throw new RpcError(error.message, error.code);
    return Array.isArray(data) ? (data as string[]) : [];
  }

  /** Set, replace or (null) remove the mark of one value (set_clicks_mark). */
  public async setMark(field: string, value: string, mark: string | null): Promise<void> {
    const { error } = await looseRpc("set_clicks_mark", { p_dim: field, p_value: value, p_mark: mark });
    if (error) throw new RpcError(error.message, error.code);
  }
}

export const clicksService = ClicksService.getInstance();
